import { DatePipe } from '@angular/common'
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core'
import { FormsModule } from '@angular/forms'
import { TranslatePipe } from '@ngx-translate/core'
import { NzSkeletonComponent } from 'ng-zorro-antd/skeleton'
import { NzSwitchComponent } from 'ng-zorro-antd/switch'
import { map, Observable, Subscription, tap } from 'rxjs'

import {
  ActiveSorting,
  TableBaseAbstract,
} from '../../abstracts/table-base.abstract'
import { TypedTemplateDirective } from '../../directives/typed-template.directive'
import { ValidatedAddress, ValidationFlag } from '../../models/address.model'
import { Branch } from '../../models/branch.model'
import { HighLightTextPipe } from '../../pipes/high-light-text.pipe'
import { MergeColumnsPipe } from '../../pipes/merge-columns.pipe'
import { AddressRepository } from '../../repositories/address/address.repository'
import { BranchService } from '../../services/branch.service'
import {
  SessionStoragePrefix,
  SyncWithSessionStorage,
} from '../../services/local-storage.service'
import { BranchStatusComponent } from '../branch-status/branch-status.component'
import { NumberComponent } from '../number/number.component'
import { SearchBoxComponent } from '../search-box/search-box.component'
import {
  TableColumn,
  TableComponent,
  TableDataBase,
} from '../table/table.component'
import { ValueFilterComponent } from '../value-filter/value-filter.component'

export type ValidationCounts = {
  totalCount: number
  totalCountAll: number
  totalUnverifiedAddresses: number
  totalConflictAddresses: number
  totalDuplicateWithChanges: number
  totalDuplicateInCsv: number
  totalNewAddresses: number
}

export type ActiveValidation = 'all' | ValidationFlag

export type BranchDetailBaseEventParams = {
  isLoading: boolean
  total: number
  offset: number
}

export type TableData = TableDataBase & ValidatedAddress

export const ActiveSortingDefault: ActiveSorting = [
  {
    key: 'plz',
    value: 'ascend',
  },
]

@SessionStoragePrefix('branchDetails')
@Component({
  selector: 'app-branch-detail-base',
  imports: [
    TableComponent,
    TranslatePipe,
    DatePipe,
    HighLightTextPipe,
    NumberComponent,
    MergeColumnsPipe,
    TypedTemplateDirective,
    SearchBoxComponent,
    ValueFilterComponent,
    NzSkeletonComponent,
    BranchStatusComponent,
    NzSwitchComponent,
    FormsModule,
  ],
  templateUrl: './branch-detail-base.component.html',
})
export class BranchDetailBaseComponent
  extends TableBaseAbstract
  implements OnInit, OnDestroy
{
  @Input({ required: true }) public id!: string
  @Input({ required: true }) public branch?: Branch
  @Input({ required: true }) public offset!: number
  @Input({ required: true }) public tableColumns!: TableColumn[]
  @Input({ required: true }) public filterAllLabel!: string
  @Input({ required: false }) public showResolvedSwitch: boolean = false

  @Output() public changeHandler =
    new EventEmitter<BranchDetailBaseEventParams>()

  private branchesSubscription!: Subscription

  public typeToken!: { item: string; currentRow: TableData }
  public tableData: TableData[] = []

  @SyncWithSessionStorage({ default: ActiveSortingDefault })
  public override activeSorting!: ActiveSorting

  // filter
  @SyncWithSessionStorage({ default: 'all' })
  public activeValidation!: ActiveValidation

  @SyncWithSessionStorage({ default: '' })
  public override sword!: string

  @SyncWithSessionStorage({ default: true })
  public showOnlyConflicts!: boolean

  public validationCounts: ValidationCounts = {
    totalCount: 0,
    totalCountAll: 0,
    totalUnverifiedAddresses: 0,
    totalConflictAddresses: 0,
    totalDuplicateWithChanges: 0,
    totalDuplicateInCsv: 0,
    totalNewAddresses: 0,
  }

  public constructor(
    private readonly addressRepository: AddressRepository,
    private readonly branchService: BranchService
  ) {
    super()
  }

  public ngOnInit(): void {
    this.pageIndex = Math.floor((this.offset - 1) / this.pageSize) + 1
    this.branchesSubscription = this.fetchTableData().subscribe()
  }

  public ngOnDestroy(): void {
    this.branchesSubscription?.unsubscribe()
  }

  public onValidationFilterChange(state: string): void {
    this.activeValidation = state as ActiveValidation
    this.pageIndex = 1
    this.updateTableData()
  }

  protected updateTableData(): void {
    this.isLoading = true
    this.branchesSubscription = this.fetchTableData().subscribe()
    this.emitOnChange()
  }

  public onSearchFilterChange(sword: string): void {
    this.sword = sword
    this.updateTableData()
  }

  private fetchTableData(): Observable<unknown> {
    return this.addressRepository
      .getBranchAddresses(this.id, {
        limit: this.pageSize,
        page: this.pageIndex,
        filter: this.branchService.getFilter(
          this.sword,
          this.activeValidation,
          this.showResolvedSwitch && this.showOnlyConflicts
        ),
        sorting: this.branchService.getSorting(this.activeSorting),
      })
      .pipe(
        tap((response) => {
          this.total = response.meta.totalCount
          this.validationCounts = response.meta
        }),
        map((res) =>
          res.data.map(
            (address) =>
              ({
                id: address.id,
                ...address.attributes,
              }) as TableData
          )
        ),
        tap((tableData) => (this.tableData = tableData)),
        tap(() => (this.isLoading = false)),
        tap(() => this.emitOnChange())
      )
  }

  private emitOnChange(): void {
    this.changeHandler.emit({
      isLoading: this.isLoading,
      total: this.total,
      offset: (this.pageIndex - 1) * this.pageSize,
    })
  }

  public onShowOnlyConflicts(): void {
    this.updateTableData()
  }
}
